import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

import { API } from "@/lib/apiBase";
const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setFavoriteIds([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/favorites/ids`);
      setFavoriteIds(res.data.ids || []);
    } catch (err) {
      console.error("Failed to load favorites", err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = useCallback(
    async (wineId) => {
      if (!user) {
        toast.error("Sign in to save favorites");
        return false;
      }
      try {
        const res = await axios.post(`${API}/favorites/${wineId}`);
        setFavoriteIds((prev) =>
          res.data.favorited ? [...prev, wineId] : prev.filter((id) => id !== wineId)
        );
        toast.success(res.data.favorited ? "Added to favorites" : "Removed from favorites");
        return res.data.favorited;
      } catch (err) {
        console.error("Failed to toggle favorite", err);
        toast.error("Could not update favorite");
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const isFavorite = useCallback((wineId) => favoriteIds.includes(wineId), [favoriteIds]);

  const value = useMemo(
    () => ({ favoriteIds, loading, toggle, isFavorite, refresh }),
    [favoriteIds, loading, toggle, isFavorite, refresh]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export const useFavorites = () => useContext(FavoritesContext);
